<template>
  <EditDialog
    :visible="showModal"
    :title="title"
    :width="600"
    :mask-closable="false"
    :sure-btn-text="sureBtnText"
    :padding="30"
    :loading="loading"
    @close="handleDialogClose"
    @submit="handleSubmit"
  >
    <div class="dialog-body">
      <DynamicsForm
        :model="voiceAgentInfo"
        :setting="formSetting"
        :label-width="110"
        class="voice-agent-form"
        layout="labelTop"
        :group-collapse="false"
        tips-placement="left"
        tips-icon="USolidHelpCircle"
      >
        <template #robot_name>
          <div class="bot-name-wrap">
            <div class="bot-icon">
              <AgentAvatarPicker
                :key="voiceAgentInfo.avatar_name"
                :icon="+voiceAgentInfo.avatar_name"
                @select="handleAvatarSubmit"
              />
            </div>
            <u-input
              v-model="voiceAgentInfo.robot_name"
              :maxlength="50"
              show-word-limit
              :placeholder="
                $t(
                  'views.voiceAgent.setting.components.AddVoiceAgent.485de23b201c4b162de48e3ed4b6435d'
                )
              "
            />
          </div>
        </template>
      </DynamicsForm>
    </div>
  </EditDialog>
</template>

<script>
import { useI18n } from 'vue-i18n';
import { defineComponent, ref, computed } from 'vue';
import { DynamicsForm } from '@koi-design/vix-components';
import { message } from '@koi-design/uxd-ui';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { createVoiceAgentApi, updateVoiceAgentApi } from '@/api/voiceAgent';
import AgentAvatarPicker from '@/components/AgentAvatar/picker.vue';

export default defineComponent({
  name: 'AddVoiceAgent',
  components: {
    DynamicsForm,
    AgentAvatarPicker
  },
  props: {
    botInfo: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['update'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const router = useRouter();
    const store = useStore();
    const homelandInfo = computed(() => store.getters.homelandInfo);
    const showModal = ref(false);
    const loading = ref(false);
    const currentType = ref('add');
    const title = ref(
      t(
        'views.voiceAgent.setting.components.AddVoiceAgent.3bc06ce1ffc323b930762e440ec7e663'
      )
    );
    const sureBtnText = ref(
      t(
        'views.voiceAgent.setting.components.AddVoiceAgent.bf355b496fb9598c21af1edcb8bde181'
      )
    );
    const voiceAgentInfo = ref({
      robot_name: '',
      avatar_name: '1',
      description: ''
    });
    const formSetting = ref([
      {
        label: t(
          'views.voiceAgent.setting.components.AddVoiceAgent.6f2fedeecf8cc7ce80082663c8c169c2'
        ),
        name: 'robot_name',
        type: 'slot',
        slot: 'robot_name',
        props: {
          maxlength: 50,
          showWordLimit: true,
          placeholder: t(
            'views.voiceAgent.setting.components.AddVoiceAgent.485de23b201c4b162de48e3ed4b6435d'
          )
        },
        rules: [
          {
            required: true,
            message: '',
            trigger: 'change'
          }
        ]
      },
      {
        label: t(
          'views.voiceAgent.setting.components.AddVoiceAgent.1f53b730ba4f13b57a39382e4d796375'
        ),
        name: 'description',
        type: 'textarea',
        props: {
          showWordLimit: true,
          maxlength: 300,
          rows: 2,
          placeholder: t(
            'views.voiceAgent.setting.components.AddVoiceAgent.cb430acc4baf87649e3658c108f90835'
          )
        }
      }
    ]);
    const handleAvatarSubmit = data => {
      voiceAgentInfo.value.avatar_name = data.icon.toString();
    };
    const show = (type = 'add') => {
      currentType.value = type;
      if (type === 'add') {
        title.value = t(
          'views.voiceAgent.setting.components.AddVoiceAgent.3bc06ce1ffc323b930762e440ec7e663'
        );
        sureBtnText.value = t(
          'views.voiceAgent.setting.components.AddVoiceAgent.bf355b496fb9598c21af1edcb8bde181'
        );
        voiceAgentInfo.value = {
          robot_name: '',
          avatar_name: '1',
          description: ''
        };
      } else if (type === 'edit') {
        title.value = t(
          'views.voiceAgent.setting.components.AddVoiceAgent.3187bc4955a5da336d7a306442b0e87a'
        );
        sureBtnText.value = t(
          'views.voiceAgent.setting.components.AddVoiceAgent.ae1b7854323e03f7a40c15bdb697e367'
        );
        voiceAgentInfo.value = {
          ...voiceAgentInfo.value,
          id: props.botInfo.id,
          robot_name: props.botInfo.robot_name,
          avatar_name: props.botInfo.avatar_name || '1',
          description: props.botInfo.description || ''
        };
      }
      showModal.value = true;
    };
    const handleDialogClose = () => {
      showModal.value = false;
    };
    const handleSubmit = async () => {
      if (!voiceAgentInfo.value.robot_name?.trim()) {
        message.error(
          t(
            'views.voiceAgent.setting.components.AddVoiceAgent.485de23b201c4b162de48e3ed4b6435d'
          )
        );
        return;
      }
      loading.value = true;
      if (currentType.value === 'edit') {
        try {
          await updateVoiceAgentApi({
            ...props.botInfo,
            robot_name: voiceAgentInfo.value.robot_name,
            avatar_name: voiceAgentInfo.value.avatar_name,
            description: voiceAgentInfo.value.description,
            homeland_id: homelandInfo.value.homeland
          });
          message.success(
            t(
              'views.voiceAgent.setting.components.AddVoiceAgent.5bda9c5b12e80031b5a89f2e85c503c4'
            )
          );
          emit('update', {
            robot_name: voiceAgentInfo.value.robot_name,
            avatar_name: voiceAgentInfo.value.avatar_name,
            description: voiceAgentInfo.value.description
          });
          showModal.value = false;
        } catch (error) {
          message.error(
            error?.message ||
              t(
                'views.voiceAgent.setting.components.AddVoiceAgent.0dc9d0a49a00dd0e8cf8a147393ddef8'
              )
          );
        } finally {
          loading.value = false;
        }
        return;
      }
      try {
        const data = await createVoiceAgentApi({
          ...voiceAgentInfo.value,
          homeland_id: homelandInfo.value.homeland,
          language: 'zh'
        });
        if (!data) {
          loading.value = false;
          return;
        }
        const { voice_robot_id } = data;
        router.push({
          path: '/voiceAgentSetting',
          query: {
            id: voice_robot_id
          }
        });
        showModal.value = false;
        loading.value = false;
      } catch (error) {
        message.error(
          error?.message ||
            t(
              'views.voiceAgent.setting.components.AddVoiceAgent.74ce0e8153e41e8f5d7ba7ddb52b43db'
            )
        );
        loading.value = false;
      }
    };
    return {
      showModal,
      loading,
      voiceAgentInfo,
      formSetting,
      title,
      sureBtnText,
      show,
      handleDialogClose,
      handleSubmit,
      handleAvatarSubmit
    };
  }
});
</script>

<style lang="less" scoped>
.dialog-body {
  .bot-name-wrap {
    display: flex;
    .bot-icon {
      margin-right: 8px;
      width: 36px;
      height: 36px;
      border-radius: 8px;
      overflow: hidden;
      position: relative;
      flex-shrink: 0;
    }
  }
}
</style>
